import {
  beMessApiGetAllMessages,
  beMessApiGetActiveMessage
} from 'Api/BeMessApi'
import { beStatApiExportEmailMessageList } from 'Api/BeStatApi'
import { beSendGoApiExistSendingRunningMessage } from 'Api/beSendGoApi'
import { storeGet } from 'Helpers/localStorage'
import { isUnsignedInteger } from 'Helpers/validators'
import { handleFrom, handleTo } from 'Helpers/dates'

import {
  STORE_KEY_ID_LIST,
  STORE_KEY_LIST,
  SERVICE_TYPE_CONSOLE,
  SERVICE_TYPE_ONETOMANY,
  MESSAGE_TYPE_GENERIC,
  MESSAGE_TYPE_CONFIRMATION,
  MESSAGE_TYPE_WELCOME,
  MESSAGE_LIST_TYPE_ARCHIVED,
  MESSAGE_LIST_TYPE_NOT_ARCHIVED,
  MESSAGE_LIST_TYPE_NOTIFICATION,
  MESSAGE_LIST_TYPE_PUBLISHED,
  COLLABORATION_PROJECT_STATUS_OPEN,
  COLLABORATION_PROJECT_STATUS_APPROVED,
  COLLABORATION_PROJECT_STATUS_ARCHIVED
} from 'Constants'
import { DEFAULT_PAGE_SIZE } from '../../constants'
import {
  beSubscriptionFlowCheckSubscriptionFlowConfigurationByListOfConfirmMsg,
  beSubscriptionFlowCheckSubscriptionFlowConfigurationByListOfWelcomeMsg
} from 'Api/beSubscriptionFlow'
import {
  beEmailMessageApiGetEntitiesThatUseMessage,
  beEmailMessageApiGetMessageHtmlExport,
  beEmailMessageApiGetMessagePdfExport
} from 'Api/beEmailMessageApi'

import { collaborationApiGetAccountProjects } from 'Api/collaborationApi'

import {
  MESSAGE_STR_TYPE_CONFIRMATION,
  MESSAGE_STR_TYPE_WELCOME
} from 'Constants/'
import { evalCondition } from 'mailup-react-helpers'
import { BeListApiGetAllTags } from 'Api/BeListApi'

function makeConditionSearchText(searchText, searchInContent) {
  const trimmedSearchText = searchText
    .trim()
    .replace(/\\/g, '\\\\')
    .replace(/'/g, "\\'")
  const conditions = [
    `Note.Contains('${trimmedSearchText}')`,
    `Subject.Contains('${trimmedSearchText}')`,
    isUnsignedInteger(trimmedSearchText)
      ? `Id == ${trimmedSearchText}`
      : undefined,
    searchInContent ? `Text.Contains('${trimmedSearchText}')` : undefined
  ]
    .filter(o => !!o)
    .join(' || ')

  return [`(${conditions})`]
}

function makeConditionFilterFrom(fieldName, date) {
  const tz = storeGet(STORE_KEY_LIST).TimeZoneCode
  const strDate = handleFrom(date, tz)
  return strDate ? [`( ${fieldName} >= #${strDate}#)`] : []
}

function makeConditionFilterTo(fieldName, date) {
  const tz = storeGet(STORE_KEY_LIST).TimeZoneCode
  const strDate = handleTo(date, tz)
  return strDate ? [`( ${fieldName} <= #${strDate}#)`] : []
}

function getServiceTypeConditions() {
  return [
    `((ServiceType == ${SERVICE_TYPE_CONSOLE}) || (ServiceType == ${SERVICE_TYPE_ONETOMANY}))`
  ]
}

function getMessageTypeConditions(type) {
  if (type === MESSAGE_LIST_TYPE_NOTIFICATION) {
    return [
      `((MessageType == ${MESSAGE_TYPE_CONFIRMATION}) || (MessageType == ${MESSAGE_TYPE_WELCOME}))`
    ]
  } else {
    return [
      `((MessageType == ${MESSAGE_TYPE_GENERIC}) || (MessageType == ${MESSAGE_TYPE_CONFIRMATION}) || (MessageType == ${MESSAGE_TYPE_WELCOME}))`
    ]
  }
}

function makeTagsConditions(tags) {
  const conditions = tags
    .map(tag => `(Tags.Contains(${parseInt(tag)}))`)
    .join(' && ')
  return [`(${conditions})`]
}

function makeOrderBy(orderBy) {
  switch (orderBy) {
    case 'LastUpdated DESC':
    case 'LastSent DESC':
    case 'Subject ASC':
    case 'Subject DESC':
      return orderBy
    default:
      return undefined
  }
}

function makeTypeConditions(type) {
  switch (type) {
    case MESSAGE_LIST_TYPE_ARCHIVED:
      return ['(Archived == true)']
    case MESSAGE_LIST_TYPE_NOT_ARCHIVED:
      return ['(Archived == false)']
    case MESSAGE_LIST_TYPE_PUBLISHED:
      return ['(Published == true)']
    default:
      return []
  }
}

export async function fetchMessages(params) {
  const idList = parseInt(storeGet(STORE_KEY_ID_LIST))

  if (!params) {
    const conditions = [
      ...getServiceTypeConditions(),
      ...getMessageTypeConditions(),
      ...makeTypeConditions(MESSAGE_LIST_TYPE_NOT_ARCHIVED)
    ]
    return beMessApiGetAllMessages(
      idList,
      conditions.join(' && '),
      makeOrderBy('LastUpdated DESC'),
      1,
      DEFAULT_PAGE_SIZE
    )
  }

  const {
    searchText,
    searchInContent,
    tags,
    type,
    filterByField,
    filterByFieldFrom,
    filterByFieldTo,
    orderBy,
    pageNumber,
    pageSize
  } = params

  const conditions = [
    ...getServiceTypeConditions(),
    ...getMessageTypeConditions(type),
    ...(searchText ? makeConditionSearchText(searchText, searchInContent) : []),
    ...(tags && tags.length ? makeTagsConditions(tags) : []),
    ...makeTypeConditions(type),
    ...makeConditionFilterFrom(filterByField, filterByFieldFrom),
    ...makeConditionFilterTo(filterByField, filterByFieldTo)
  ]

  const apiParams = new Map()
  apiParams.set('idList', idList)
  apiParams.set('conditions', conditions.join(' && '))
  apiParams.set('orderBy', makeOrderBy(orderBy))
  apiParams.set('pageNumber', pageNumber)
  apiParams.set('pageSize', pageSize)

  const res = await beMessApiGetAllMessages(...apiParams.values())
  const pageNotExist = !res.Items.length && res.TotalItems && res.PageNumber > 1

  if (pageNotExist) {
    apiParams.set('pageNumber', Math.ceil(res.TotalItems / pageSize))
    return beMessApiGetAllMessages(...apiParams.values())
  }

  return res
}

export const exportEmailMessages = (search, exportType) => {
  const idList = parseInt(storeGet(STORE_KEY_ID_LIST))

  const exportParams = {
    IdList: idList,
    MessageType: [
      MESSAGE_TYPE_GENERIC,
      MESSAGE_TYPE_WELCOME,
      MESSAGE_TYPE_CONFIRMATION
    ],
    ServiceType: [SERVICE_TYPE_CONSOLE, SERVICE_TYPE_ONETOMANY],
    ExportType: exportType
  }

  return beStatApiExportEmailMessageList(1, 0, exportParams)
}

export async function getActiveMessage(messageType) {
  const idList = parseInt(storeGet(STORE_KEY_ID_LIST))
  return beMessApiGetActiveMessage(idList, messageType)
}

export async function getMessagesWithForm(items) {
  if (!items.length) return []

  const confirmation = items
    .filter(({ MessageType }) => MessageType === MESSAGE_STR_TYPE_CONFIRMATION)
    .map(item => item.Id)

  const welcome = items
    .filter(({ MessageType }) => MessageType === MESSAGE_STR_TYPE_WELCOME)
    .map(item => item.Id)

  const [{ IdsConfirmMsg }, { IdsWelcomeMsg }] = await Promise.all([
    confirmation.length > 0
      ? beSubscriptionFlowCheckSubscriptionFlowConfigurationByListOfConfirmMsg(
          confirmation
        )
      : { IdsConfirmMsg: [] },
    welcome.length > 0
      ? beSubscriptionFlowCheckSubscriptionFlowConfigurationByListOfWelcomeMsg(
          welcome
        )
      : { IdsWelcomeMsg: [] }
  ])

  return Array.from(new Set([...IdsConfirmMsg, ...IdsWelcomeMsg]))
}

export async function getEntitiesThatUseMessage(idMessage) {
  const res = await beEmailMessageApiGetEntitiesThatUseMessage(idMessage)

  return res
}

export async function getMessageHtmlExport(idMessage) {
  const res = await beEmailMessageApiGetMessageHtmlExport(idMessage)

  return res
}

export async function getMessagePdfExport(idMessage) {
  const res = await beEmailMessageApiGetMessagePdfExport(idMessage)

  return res
}

export function fetchCollaborationResults(ids) {
  const collaborationEnabled = evalCondition('uo:COLLABORATIONS')

  const projectsIds = ids.map(idMessage => `NL_${idMessage}`)

  return collaborationEnabled && projectsIds.length
    ? collaborationApiGetAccountProjects(
        [
          COLLABORATION_PROJECT_STATUS_OPEN,
          COLLABORATION_PROJECT_STATUS_APPROVED,
          COLLABORATION_PROJECT_STATUS_ARCHIVED
        ],
        projectsIds
      )
    : Promise.resolve({ Items: [] })
}

export const getTags = async () => {
  const idList = parseInt(storeGet(STORE_KEY_ID_LIST))
  const result = await BeListApiGetAllTags(idList, true, false)
  if (result.Items.length > 0) {
    return result.Items.map(t => ({
      label: t.Name,
      value: t.Id
    }))
  }
  return [{ label: undefined, value: undefined }]
}
